package at.apa.statistictracker.data.model.statisticevent

import at.apa.statistictracker.data.model.Platform
import at.apa.statistictracker.serialization.LocalDateTimeSerializer
import at.apa.statistictracker.util.getPlatform
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable
import kotlin.js.ExperimentalJsExport
import kotlin.js.JsExport
import kotlin.js.JsName

@OptIn(ExperimentalJsExport::class)
@JsExport
@Serializable
data class MpsStatisticEvent(
    val appKey: String,
    val deviceId: String,
    val eventName: String,
    val upPlatform: Platform = getPlatform(),
    @Serializable(with = LocalDateTimeSerializer::class)
    val timestamp: LocalDateTime = Clock.System.now().toLocalDateTime(TimeZone.UTC),

    // put all fields in constructor in order to make the data class copy work out of the box.
    // If the below fields were class properties, the below used copy method would just copy the field from the constructor
    // into the newly created object: https://kotlinlang.org/docs/data-classes.html#properties-declared-in-the-class-body
    var clientVersion: String? = null,
    var articleCharacterCount: Int? = null,
    var articleCID: String? = null,
    var articleId: String? = null,
    var articleTitle: String? = null,
    var articleChapter: String? = null,
    var caller: String? = null,
    var contentUrl: String? = null,
    var issueDate: String? = null,
    var issueId: String? = null,
    var issueMutation: String? = null,
    var issueName: String? = null,
    var pageChapter: String? = null,
    var pageCID: String? = null,
    var pageId: String? = null,
    var pageNr: String? = null, // e.g. A1 or P1
    var pagePosition: String? = null, // p001, p002, ...
    var retentionTime: Int? = null,
    var readingRate: Float? = null,
    var subissueDate: String? = null,
    var subissueId: String? = null,
    var subissueMutation: String? = null,
    var subissueName: String? = null,
    // user properties are internal because they get set by the library before sending
    internal var upRegion: String? = null,
    internal var upLoggedIn: Boolean? = null,
    internal var upFreedaysActive: Boolean? = null,
    var widgetName: String? = null
) : StatisticEvent() {

    // Secondary constructor for JavaScript and native targets because there is no support for default values for parameters,
    // Hence, without this secondary constructor, JS and native targets would have to pass a value for each of the parameters.
    @JsName("jsConstructor")
    constructor(appKey: String, deviceId: String, eventName: String) : this(
        appKey,
        deviceId,
        eventName,
        getPlatform(),
        Clock.System.now().toLocalDateTime(TimeZone.UTC)
    )


    companion object {
        fun testFactory(
            appKey: String = "abc",
            deviceId: String = "123",
            eventName: String = "dummy event name"
        ) = MpsStatisticEvent(
            appKey, deviceId, eventName
        )
    }

    override fun createCopy() = this.copy()

    override fun toString(): String {
        return "MpsStatisticEvent(" +
                "appKey='$appKey', " +
                "deviceId='$deviceId', " +
                "eventName='$eventName', " +
                "upPlatform=$upPlatform, " +
                "timestamp=$timestamp, " +
                "clientVersion=$clientVersion, " +
                "articleCharacterCount=$articleCharacterCount, " +
                "articleCID=$articleCID, articleId=$articleId, " +
                "articleTitle=$articleTitle, " +
                "articleChapter=$articleChapter, " +
                "contentUrl=$contentUrl, " +
                "issueDate=$issueDate, " +
                "issueId=$issueId, " +
                "issueMutation=$issueMutation, " +
                "issueName=$issueName, " +
                "pageChapter=$pageChapter, " +
                "pageCID=$pageCID, " +
                "pageId=$pageId, " +
                "pageNr=$pageNr, " +
                "pagePosition=$pagePosition, " +
                "retentionTime=$retentionTime, " +
                "readingRate=$readingRate, " +
                "subissueDate=$subissueDate, " +
                "subissueId=$subissueId, " +
                "subissueMutation=$subissueMutation, " +
                "subissueName=$subissueName, " +
                "upRegion=$upRegion, " +
                "upLoggedIn=$upLoggedIn, " +
                "upFreedaysActive=$upFreedaysActive, " +
                "widgetName=$widgetName" +
                ")"
    }
}